import { useCallback, useEffect, useState } from 'react';
import {
  getUserLoonTransactions,
  type UserLoonTransaction,
} from '../transport/api/protectedApi';
import {
  createLogger,
  sortProductHistoryTransactionsNewestFirst,
} from '@airhead-games/shared/utilities';

const logger = createLogger('useProductHistory');

interface UseProductHistoryOptions {
  autoFetch?: boolean;
  limit?: number;
}

interface UseProductHistoryResult {
  transactions: UserLoonTransaction[];
  nextCursor: string | null;
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  loadMore: () => Promise<void>;
}

export function useProductHistory(
  options: UseProductHistoryOptions = {}
): UseProductHistoryResult {
  const { autoFetch = false, limit } = options;
  const [transactions, setTransactions] = useState<UserLoonTransaction[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(autoFetch);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getUserLoonTransactions({
        limit,
      });

      if (response.success && response.data?.transactions) {
        setTransactions(
          sortProductHistoryTransactionsNewestFirst(response.data.transactions)
        );
        setNextCursor(response.data.nextCursor ?? null);
      } else {
        setTransactions([]);
        setNextCursor(null);
        setError('Failed to load loon history.');
      }
    } catch (fetchError) {
      logger.error('Failed to load loon history', fetchError);
      setTransactions([]);
      setNextCursor(null);
      setError('Failed to load loon history.');
    } finally {
      setLoading(false);
    }
  }, [limit]);

  const loadMore = useCallback(async () => {
    if (!nextCursor || loading || loadingMore) {
      return;
    }

    setLoadingMore(true);
    setError(null);

    try {
      const response = await getUserLoonTransactions({
        limit,
        cursor: nextCursor,
      });

      if (response.success && response.data?.transactions) {
        setTransactions((previous) =>
          sortProductHistoryTransactionsNewestFirst([
            ...previous,
            ...response.data.transactions,
          ])
        );
        setNextCursor(response.data.nextCursor ?? null);
      } else {
        setError('Failed to load more loon history.');
      }
    } catch (fetchError) {
      logger.error('Failed to load more loon history', fetchError);
      setError('Failed to load more loon history.');
    } finally {
      setLoadingMore(false);
    }
  }, [limit, loading, loadingMore, nextCursor]);

  useEffect(() => {
    if (autoFetch) {
      void fetchHistory();
    }
  }, [autoFetch, fetchHistory]);

  return {
    transactions,
    nextCursor,
    loading,
    loadingMore,
    error,
    refetch: fetchHistory,
    loadMore,
  };
}
